import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { defer, EMPTY, expand, finalize, from, Observable, of, takeWhile, throwError } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';

@Injectable()
export class GaiaUserGuideService {

  /**
   * Sends a question to Gaia's user guide API and streams the response tokens as an observable.
   * Establishes a POST request to a streaming endpoint, processes the response,
   * and returns individual tokens extracted from the stream.
   * @param {string} question The question to be sent to Gaia's user guide API for obtaining guidance.
   * @return {Observable<string>} An observable that emits response tokens individually as strings until the stream ends.
   */
  public askGaiaUserGuide(question: string): Observable<string> {
    return defer(() => {
      const controller = new AbortController();
      const decoder = new TextDecoderStream();

      let reader: ReadableStreamDefaultReader<string>;
      let buffer = '';
      let finished = false;

      const read = () => defer(() => from(reader.read()));

      return from(fetch(`${environment.userGuide.baseUrl}/api-guide-mya/rag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': environment.userGuide.apiKey,
          Connection: "keep-alive",
          Accept: 'text/event-stream'
        },
        body: JSON.stringify({question}),
        signal: controller.signal
      }))
        .pipe(
          switchMap(response => {
            if (!response.ok) {
              return throwError(() => new Error(`HTTP error ${response.status}`));
            }

            if (!response.body) {
              return throwError(() => new Error('Streaming not supported'));
            }

            reader = response.body.pipeThrough(decoder).getReader();

            return read().pipe(
              // Continue reading chunks until the browser marks the stream as done or a [DONE] event is received.
              expand(result => result.done || finished ? EMPTY : read()),
              mergeMap(({value, done}) => {
                if (done || !value) {
                  finished = true;
                  return EMPTY;
                }

                buffer += value;

                // Server-sent events are separated by a blank line.
                const events = buffer.split(/\r?\n\r?\n/);
                buffer = events.pop() ?? '';

                return from(events).pipe(
                  mergeMap(event => {
                    // Extract and combine all `data:` lines from the server-sent event.
                    const data = event
                      .split(/\r?\n/)
                      .filter(line => line.startsWith('data:'))
                      .map(line => line.replace(/^data:\s?/, ''))
                      .join('\n')
                      .trim();

                    if (!data) {
                      return EMPTY;
                    }

                    // The API sends [DONE] to indicate that no more tokens will arrive.
                    if (data === '[DONE]') {
                      finished = true;
                      return EMPTY;
                    }

                    // Parse the event payload and emit only the streamed token value.
                    const parsed = JSON.parse(data);
                    return parsed.token === undefined ? EMPTY : of(parsed.token);
                  })
                );
              }),
              // Complete the observable once the stream has been marked as finished.
              takeWhile(() => !finished, true)
            );
          }),
          finalize(() => {
            // Ensure the request is cancelled and the stream reader is released on completion, error, or unsubscribe.
            controller.abort();
            reader?.releaseLock();
          })
        );
    });
  }
}
